import React, { useMemo, useState, useEffect, useRef } from 'react';
import { formatPunchTime, todayYmd, computeAttendanceDayStatus } from '@/features/attendance/services/attendancePresentation';

function normalizeDateKey(value) {
  const raw = String(value || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const dmy = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})/);
  if (dmy) {
    return `${dmy[3]}-${String(dmy[2]).padStart(2, '0')}-${String(dmy[1]).padStart(2, '0')}`;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(parsed);
  const values = Object.fromEntries(parts.map(({ type, value: partValue }) => [type, partValue]));
  return `${values.year}-${values.month}-${values.day}`;
}

function firstValue(...values) {
  return values.find((value) => String(value ?? '').trim()) ?? '';
}

function parseTimeToMinutes(timeStr) {
  if (!timeStr || timeStr === '--') return null;
  const raw = String(timeStr).trim();
  const match = raw.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (!match) return null;
  let hour = Number(match[1]);
  const min = Number(match[2]);
  const mer = match[4]?.toLowerCase();
  if (mer === 'pm' && hour < 12) hour += 12;
  if (mer === 'am' && hour === 12) hour = 0;
  return hour * 60 + min;
}

function calculateWorkingDuration(punchIn, punchOut, rawDuration) {
  if (rawDuration && rawDuration !== '-' && /\d+\s*h/i.test(String(rawDuration))) {
    return String(rawDuration).trim();
  }
  const inMin = parseTimeToMinutes(punchIn);
  const outMin = parseTimeToMinutes(punchOut);
  if (inMin === null || outMin === null) return null;
  let diff = outMin - inMin;
  if (diff < 0) diff += 24 * 60; // Overnight
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return `${h}h ${m}m`;
}

function rowAction(row = {}) {
  return String(row.Action || row.action || '').trim().toLowerCase();
}

function isPunchOutAction(row = {}) {
  return /punch\s*out/.test(rowAction(row));
}

function isPunchInAction(row = {}) {
  return /punch\s*in/.test(rowAction(row));
}

function punchInValue(row = {}) {
  return firstValue(
    row.punchIn?.['Punch In'],
    row.punchIn?.InTime,
    row.punchIn?.inTime,
    row.punchIn?.['Punch In Time'],
    row.punchIn?.Time,
    row.punchInTime,
    row['Punch In'],
    row.InTime,
    row.inTime,
    row['Punch In Time'],
    isPunchOutAction(row) ? '' : row.Time
  );
}

function punchOutValue(row = {}) {
  return firstValue(
    row.punchOut?.['Punch Out'],
    row.punchOut?.OutTime,
    row.punchOut?.outTime,
    row.punchOut?.['Punch Out Time'],
    row.punchOut?.Time,
    row.punchOutTime,
    row['Punch Out'],
    row.OutTime,
    row.outTime,
    row['Punch Out Time'],
    isPunchInAction(row) ? '' : row.Time
  );
}

function mergeAttendanceDayRows(existing = {}, incoming = {}) {
  return {
    ...existing,
    ...incoming,
    punchIn: existing.punchIn || incoming.punchIn || (punchInValue(incoming) ? incoming : null),
    punchOut: existing.punchOut || incoming.punchOut || (punchOutValue(incoming) ? incoming : null),
    punchInTime: firstValue(existing.punchInTime, incoming.punchInTime, punchInValue(existing), punchInValue(incoming)),
    punchOutTime: firstValue(existing.punchOutTime, incoming.punchOutTime, punchOutValue(existing), punchOutValue(incoming)),
    duration: firstValue(existing.duration, incoming.duration, existing.Duration, incoming.Duration)
  };
}

function hasAttendancePunch(row) {
  if (!row) return false;
  return Boolean(firstValue(punchInValue(row), punchOutValue(row)));
}

function isPlaceholderAttendanceRow(row) {
  if (!row || hasAttendancePunch(row)) return false;
  const status = String(row.status || row.Status || '').trim().toLowerCase();
  return status.includes('absent') || status.includes('weekly off') || status.includes('unknown') || status.includes('holiday');
}

function attendanceRowRank(row) {
  if (!row) return 0;
  if (/holiday/i.test(String(row.status || row.Status || '')) || row.isHoliday) return 4;
  if (hasAttendancePunch(row)) return 3;
  if (!isPlaceholderAttendanceRow(row) && String(row.status || row.Status || '').trim()) return 2;
  return 1;
}

function resolveCellStatus(row, dateStr) {
  return computeAttendanceDayStatus({
    date: dateStr,
    punchInValue: punchInValue(row),
    punchOutValue: punchOutValue(row),
    rawStatus: row?.status || row?.Status,
    isHoliday: Boolean(row?.isHoliday || /holiday/i.test(String(row?.status || row?.Status || '')))
  });
}

export function AttendanceCalendar({ rows = [], leaves = [], intimations = [], onMonthChange, onSelectDate }) {
  const seedMonthDate = useMemo(() => {
    if (rows.length > 0) {
      const firstKnownDate = rows
        .map((row) => normalizeDateKey(row.date || row.Date))
        .find((value) => /^\d{4}-\d{2}-\d{2}$/.test(value));
      if (firstKnownDate) {
        const date = new Date(`${firstKnownDate}T00:00:00`);
        if (!Number.isNaN(date.getTime())) return date;
      }
    }
    return new Date();
  }, [rows]);

  const [viewMonthDate, setViewMonthDate] = useState(seedMonthDate);
  const hasLockedInitialMonthRef = useRef(rows.length > 0);

  useEffect(() => {
    if (hasLockedInitialMonthRef.current || rows.length === 0) return;
    setViewMonthDate(seedMonthDate);
    hasLockedInitialMonthRef.current = true;
  }, [rows, seedMonthDate]);

  const displayDate = useMemo(() => {
    const d = new Date(viewMonthDate);
    return d;
  }, [viewMonthDate]);

  const year = displayDate.getFullYear();
  const month = displayDate.getMonth();

  useEffect(() => {
    if (!onMonthChange) return;
    const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(new Date(year, month + 1, 0).getDate()).padStart(2, '0')}`;
    onMonthChange({ startDate, endDate });
  }, [year, month, onMonthChange]);

  const today = new Date();
  const isCurrentMonthOrFuture = 
    year > today.getFullYear() || 
    (year === today.getFullYear() && month >= today.getMonth());

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay(); // 0 is Sunday

  // Map dates to status
  const rowMap = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      const key = normalizeDateKey(row.date || row.Date);
      if (!key) return;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, row);
        return;
      }

      const existingRank = attendanceRowRank(existing);
      const incomingRank = attendanceRowRank(row);
      if (incomingRank > existingRank) {
        map.set(key, mergeAttendanceDayRows(existing, row));
        return;
      }
      if (incomingRank === existingRank && hasAttendancePunch(row)) {
        map.set(key, mergeAttendanceDayRows(existing, row));
        return;
      }
      if (incomingRank === existingRank && !hasAttendancePunch(existing) && !isPlaceholderAttendanceRow(row)) {
        map.set(key, row);
      }
    });
    return map;
  }, [rows]);

  // Map leaves by date
  const leaveMap = useMemo(() => {
    const map = new Map();
    (leaves || []).forEach((leave) => {
      const rawStart = firstValue(leave['Start Date'], leave.startDate, leave.Date);
      const rawEnd = firstValue(leave['End Date'], leave.endDate, rawStart);
      const start = normalizeDateKey(rawStart);
      const end = normalizeDateKey(rawEnd);
      if (!start) return;

      const leaveType = firstValue(leave['Leave Type'], leave.leaveType, 'Leave');
      const dayType = firstValue(leave['Day Type'], leave.dayType, 'Full Day');
      const status = firstValue(leave.Status, leave.status, 'Pending');
      const reason = firstValue(leave.Reason, leave.reason, '');

      const sD = new Date(`${start}T00:00:00`);
      const eD = new Date(`${end || start}T00:00:00`);
      if (Number.isNaN(sD.getTime())) return;

      const curr = new Date(sD);
      const limit = Number.isNaN(eD.getTime()) ? sD : eD;
      let count = 0;
      while (curr <= limit && count < 60) {
        const key = `${curr.getFullYear()}-${String(curr.getMonth() + 1).padStart(2, '0')}-${String(curr.getDate()).padStart(2, '0')}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push({
          leaveType,
          dayType,
          status,
          reason,
          isApproved: /approved|accepted/i.test(status),
          isPending: /pending|applied|submitted/i.test(status),
          isRejected: /rejected/i.test(status)
        });
        curr.setDate(curr.getDate() + 1);
        count++;
      }
    });
    return map;
  }, [leaves]);

  // Map intimations by date
  const intimationMap = useMemo(() => {
    const map = new Map();
    (intimations || []).forEach((item) => {
      const rawDate = firstValue(item['Intimation Date'], item.Date, item.date);
      const dateKey = normalizeDateKey(rawDate);
      if (!dateKey) return;

      const intimationType = firstValue(item['Intimation Type'], item.IntimationType, item.type, 'Intimation');
      const status = firstValue(item.Status, item.status, 'Pending');
      const reason = firstValue(item.Reason, item.reason, '');

      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey).push({
        intimationType,
        status,
        reason,
        isApproved: /approved|accepted/i.test(status),
        isPending: /pending|applied|submitted/i.test(status),
        isRejected: /rejected/i.test(status)
      });
    });
    return map;
  }, [intimations]);

  // Generate calendar grid
  const days = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  const formatTime = (rawTime) => {
    if (!rawTime) return null;
    return formatPunchTime(rawTime);
  };

  for (let i = 1; i <= daysInMonth; i++) {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
    const rowObj = rowMap.get(dateStr);
    const dayLeaves = leaveMap.get(dateStr) || [];
    const dayIntimations = intimationMap.get(dateStr) || [];

    const punchIn = formatTime(punchInValue(rowObj));
    const punchOut = formatTime(punchOutValue(rowObj));
    const duration = calculateWorkingDuration(punchIn, punchOut, firstValue(rowObj?.duration, rowObj?.Duration, rowObj?.['Total Duration']));

    let status = resolveCellStatus(rowObj, dateStr);

    const hasApprovedLeave = dayLeaves.some((l) => l.isApproved);
    const hasPendingLeave = dayLeaves.some((l) => l.isPending);
    const approvedLeave = dayLeaves.find((l) => l.isApproved);
    const pendingLeave = dayLeaves.find((l) => l.isPending);

    const hasApprovedIntimation = dayIntimations.some((i) => i.isApproved);
    const hasPendingIntimation = dayIntimations.some((i) => i.isPending);
    const approvedIntimation = dayIntimations.find((i) => i.isApproved);
    const pendingIntimation = dayIntimations.find((i) => i.isPending);

    const isSunday = new Date(`${dateStr}T00:00:00`).getDay() === 0;
    const isPastOrToday = dateStr <= todayYmd();

    // Adjust status when leave / intimation applies
    if (!rowObj || status === 'Absent' || status === 'Unknown') {
      if (hasApprovedLeave) {
        status = /half/i.test(approvedLeave.dayType) ? 'Half Day' : (approvedLeave.leaveType || 'Leave');
      } else if (hasApprovedIntimation && /work\s*from\s*home|others?|wfh/i.test(approvedIntimation.intimationType)) {
        status = 'Others';
      } else if (hasApprovedIntimation && /visit/i.test(approvedIntimation.intimationType)) {
        status = 'Client Visit';
      } else if (hasApprovedIntimation && /miss\s*punch/i.test(approvedIntimation.intimationType)) {
        status = 'Present';
      } else if (hasPendingLeave) {
        status = `${pendingLeave.leaveType || 'Leave'} (Applied)`;
      } else if (hasPendingIntimation) {
        status = `${/work\s*from\s*home|wfh/i.test(pendingIntimation.intimationType) ? 'Others' : (pendingIntimation.intimationType || 'Intimation')} (Applied)`;
      } else if (!rowObj) {
        status = isSunday ? 'Weekly Off' : (isPastOrToday ? 'Absent' : 'Unknown');
      }
    }

    days.push({
      day: i,
      date: dateStr,
      status,
      punchIn,
      punchOut,
      duration,
      leaves: dayLeaves,
      intimations: dayIntimations
    });
  }

  const getStatusColor = (status) => {
    const s = String(status).toLowerCase();
    if (s.includes('applied')) return {
      bg: 'linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%)',
      border: 'rgba(245, 158, 11, 0.35)',
      text: '#9a3412',
      badgeBg: '#ea580c',
      badgeText: '#ffffff',
      durationColor: '#9a3412',
      durationBg: 'rgba(234, 88, 12, 0.12)'
    };
    if (s.includes('wfh') || s.includes('home') || s.includes('other')) return {
      bg: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
      border: 'rgba(14, 165, 233, 0.35)',
      text: '#0369a1',
      badgeBg: '#0284c7',
      badgeText: '#ffffff',
      durationColor: '#0369a1',
      durationBg: 'rgba(2, 132, 199, 0.12)'
    };
    if (
      s.includes('present') || 
      s.includes('on time') || 
      s.includes('late') || 
      s.includes('early')
    ) return { 
      bg: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', 
      border: 'rgba(34, 197, 94, 0.3)', 
      text: '#166534',
      badgeBg: '#16a34a',
      badgeText: '#ffffff',
      durationColor: '#15803d',
      durationBg: 'rgba(22, 163, 74, 0.15)'
    };
    if (s.includes('absent')) return { 
      bg: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', 
      border: 'rgba(239, 68, 68, 0.3)', 
      text: '#991b1b',
      badgeBg: '#dc2626',
      badgeText: '#ffffff',
      durationColor: '#991b1b',
      durationBg: 'rgba(220, 38, 38, 0.12)'
    };
    if (s.includes('half')) return { 
      bg: 'linear-gradient(135deg, #fef9c3 0%, #fef08a 100%)', 
      border: 'rgba(234, 179, 8, 0.3)', 
      text: '#854d0e',
      badgeBg: '#d97706',
      badgeText: '#ffffff',
      durationColor: '#b45309',
      durationBg: 'rgba(217, 119, 6, 0.15)'
    };
    if (s.includes('leave') || s.includes('off') || s.includes('holiday')) return { 
      bg: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)', 
      border: 'rgba(99, 102, 241, 0.3)', 
      text: '#3730a3',
      badgeBg: '#4f46e5',
      badgeText: '#ffffff',
      durationColor: '#4338ca',
      durationBg: 'rgba(79, 70, 229, 0.12)'
    };
    return { 
      bg: '#ffffff', 
      border: '#e2e8f0', 
      text: '#64748b',
      badgeBg: 'transparent',
      badgeText: 'transparent',
      durationColor: '#64748b',
      durationBg: '#f1f5f9'
    };
  };

  const monthName = displayDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="attendance-calendar-container" style={{
      background: '#ffffff',
      borderRadius: '24px',
      padding: '24px',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)',
      border: '1px solid #f1f5f9'
    }}>
      <div className="attendance-calendar-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button 
            type="button"
            onClick={() => setViewMonthDate(new Date(year, month - 1, 1))}
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '6px 12px',
              cursor: 'pointer',
              color: '#64748b',
              fontWeight: '600'
            }}
          >
            &lt;
          </button>
          <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
            {monthName}
          </h3>
          <button 
            type="button"
            onClick={() => setViewMonthDate(new Date(year, month + 1, 1))}
            disabled={isCurrentMonthOrFuture}
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '6px 12px',
              cursor: isCurrentMonthOrFuture ? 'not-allowed' : 'pointer',
              color: isCurrentMonthOrFuture ? '#cbd5e1' : '#64748b',
              fontWeight: '600'
            }}
          >
            &gt;
          </button>
          {!isCurrentMonthOrFuture && (
            <button
              type="button"
              onClick={() => setViewMonthDate(new Date())}
              style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '8px',
                padding: '6px 10px',
                cursor: 'pointer',
                color: '#2563eb',
                fontSize: '12px',
                fontWeight: '700'
              }}
            >
              Today
            </button>
          )}
        </div>

        <div className="attendance-calendar-legend" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }}></span> Present
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }}></span> Absent
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b' }}></span> Half Day
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#6366f1' }}></span> Leave/Off
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ea580c' }}></span> Applied / Intimation
          </div>
        </div>
      </div>

      <div className="attendance-calendar-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '12px',
        textAlign: 'center'
      }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={{ fontSize: '12px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', paddingBottom: '8px' }}>
            {day}
          </div>
        ))}

        {days.map((dayObj, index) => {
          if (!dayObj) {
            return <div key={`empty-${index}`} style={{ padding: '12px' }}></div>;
          }

          const colors = getStatusColor(dayObj.status);
          const isKnown = dayObj.status !== 'Unknown';

          return (
            <div
              key={dayObj.date}
              className="attendance-calendar-cell"
              onClick={() => {
                if (onSelectDate && dayObj.date) {
                  onSelectDate(dayObj.date);
                }
              }}
              title={onSelectDate ? `Click to apply Miss Punch / Intimation for ${dayObj.date}` : undefined}
              style={{
                minHeight: '128px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: colors.bg,
                border: `1px solid ${colors.border}`,
                borderRadius: '16px',
                padding: '8px 4px',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: onSelectDate ? 'pointer' : 'default',
                position: 'relative',
                boxShadow: isKnown ? '0 2px 4px rgba(0,0,0,0.02)' : 'none'
              }}
              onMouseEnter={(e) => { 
                e.currentTarget.style.transform = 'translateY(-4px)'; 
                e.currentTarget.style.boxShadow = '0 12px 20px -8px rgba(0,0,0,0.15)'; 
                e.currentTarget.style.borderColor = 'transparent';
              }}
              onMouseLeave={(e) => { 
                e.currentTarget.style.transform = 'translateY(0)'; 
                e.currentTarget.style.boxShadow = isKnown ? '0 2px 4px rgba(0,0,0,0.02)' : 'none'; 
                e.currentTarget.style.borderColor = colors.border;
              }}
            >
              <span style={{ fontSize: '18px', fontWeight: '800', color: colors.text, marginBottom: isKnown ? '4px' : '0' }}>
                {dayObj.day}
              </span>
              {isKnown && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center', width: '100%' }}>
                  {/* Status Pill Badge */}
                  <span className="attendance-calendar-badge" style={{ 
                    fontSize: '10px', 
                    fontWeight: '700', 
                    color: colors.badgeText, 
                    textTransform: 'uppercase', 
                    letterSpacing: '0.04em', 
                    background: colors.badgeBg, 
                    padding: '3px 8px', 
                    borderRadius: '12px', 
                    textAlign: 'center', 
                    lineHeight: '1.1',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.08)'
                  }}>
                    {dayObj.status}
                  </span>

                  {/* Punch In / Out times */}
                  {(dayObj.punchIn || dayObj.punchOut) && (
                    <div style={{ fontSize: '11px', fontWeight: '800', color: colors.text, opacity: 1, textAlign: 'center', marginTop: '2px', display: 'flex', flexDirection: 'column', gap: '1px', lineHeight: '1.2' }}>
                      <span>In: {dayObj.punchIn || '--'}</span>
                      <span>Out: {dayObj.punchOut || '--'}</span>
                    </div>
                  )}

                  {/* Working Duration between punch in and punch out */}
                  {dayObj.duration && (
                    <div 
                      style={{ 
                        fontSize: '11px', 
                        fontWeight: '800', 
                        color: colors.durationColor || '#0369a1', 
                        background: colors.durationBg || 'rgba(14, 165, 233, 0.12)', 
                        padding: '2px 6px',
                        borderRadius: '6px',
                        marginTop: '2px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '3px',
                        lineHeight: '1.2'
                      }}
                      title={`Worked Duration: ${dayObj.duration}`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      <span>{dayObj.duration}</span>
                    </div>
                  )}

                  {/* Applied / Approved Leaves */}
                  {dayObj.leaves?.map((l, lIdx) => {
                    const isApproved = l.isApproved;
                    return (
                      <span
                        key={`leave-${lIdx}`}
                        style={{
                          fontSize: '9px',
                          fontWeight: '700',
                          color: isApproved ? '#4338ca' : '#b45309',
                          background: isApproved ? 'rgba(99, 102, 241, 0.15)' : 'rgba(245, 158, 11, 0.18)',
                          border: isApproved ? '1px solid rgba(99, 102, 241, 0.35)' : '1px solid rgba(245, 158, 11, 0.4)',
                          padding: '2px 5px',
                          borderRadius: '6px',
                          marginTop: '2px',
                          maxWidth: '94%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px'
                        }}
                        title={`Leave: ${l.leaveType} (${l.dayType}) | Status: ${l.status}${l.reason ? ` | Reason: ${l.reason}` : ''}`}
                      >
                        <span>{isApproved ? '🏖️' : '⏳'}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.leaveType} {isApproved ? '✓' : '(Applied)'}</span>
                      </span>
                    );
                  })}

                  {/* Applied / Approved Intimations */}
                  {dayObj.intimations?.map((intItem, iIdx) => {
                    const isApproved = intItem.isApproved;
                    return (
                      <span
                        key={`int-${iIdx}`}
                        style={{
                          fontSize: '9px',
                          fontWeight: '700',
                          color: isApproved ? '#047857' : '#0369a1',
                          background: isApproved ? 'rgba(16, 185, 129, 0.15)' : 'rgba(14, 165, 233, 0.15)',
                          border: isApproved ? '1px solid rgba(16, 185, 129, 0.35)' : '1px solid rgba(14, 165, 233, 0.4)',
                          padding: '2px 5px',
                          borderRadius: '6px',
                          marginTop: '2px',
                          maxWidth: '94%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px'
                        }}
                        title={`Intimation: ${intItem.intimationType} | Status: ${intItem.status}${intItem.reason ? ` | Reason: ${intItem.reason}` : ''}`}
                      >
                        <span>{isApproved ? '📋' : '⏳'}</span>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{intItem.intimationType} {isApproved ? '✓' : '(Applied)'}</span>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
